/**
 * libp2p Node Setup
 *
 * Creates and configures the main libp2p node with WebRTC and WebSocket transports.
 */

import { createLibp2p, Libp2p } from 'libp2p'
import { webRTC } from '@libp2p/webrtc'
import { webSockets } from '@libp2p/websockets'
import { all } from '@libp2p/websockets/filters'
import { noise } from '@chainsafe/libp2p-noise'
import { yamux } from '@chainsafe/libp2p-yamux'
import { gossipsub } from '@chainsafe/libp2p-gossipsub'
import { bootstrap } from '@libp2p/bootstrap'
import { identify } from '@libp2p/identify'
// Ping service removed - version 3.x incompatible with libp2p 1.x (newScope error)
import { circuitRelayTransport } from '@libp2p/circuit-relay-v2'

// Bootstrap/relay nodes deployed on Fly.io
// Exported so other modules can use the same relay address
export const BOOTSTRAP_NODES: string[] = [
  // Primary relay node (stable peer ID from PEER_SEED)
  '/dns4/crowdtalkie-relay.fly.dev/tcp/4002/wss/p2p/12D3KooWC3ZHxR2HTepdYr2RG4ZQGZvPSjyBbQYj37oZBCE85wKy',
]

// Check if we're in development or production
const isDev = import.meta.env.DEV

// Log relay status on startup
if (isDev) {
  console.log('[P2P] Running in development mode - relay connection may be limited')
}

export interface P2PNodeConfig {
  bootstrapNodes?: string[]
}

let node: Libp2p | null = null

/**
 * Create and start the libp2p node
 */
export async function createP2PNode(config: P2PNodeConfig = {}): Promise<Libp2p> {
  if (node) {
    return node
  }

  const bootstrapList = config.bootstrapNodes ?? BOOTSTRAP_NODES

  // Filter out empty bootstrap nodes
  const validBootstrapList = bootstrapList.filter(addr => addr && addr.length > 0)

  console.log('[P2P] Creating node with config:', {
    bootstrapNodes: validBootstrapList.length,
    isDev,
  })

  node = await createLibp2p({
    transports: [
      // Primary: WebRTC for direct P2P connections
      webRTC(),
      // Secondary: WebSockets for relay connections
      webSockets({
        filter: all,
      }),
      // Enable circuit relay for NAT traversal
      circuitRelayTransport({
        discoverRelays: 1,
      }),
    ],
    connectionEncryption: [
      noise(),
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    streamMuxers: [yamux()] as any,
    services: {
      identify: identify(),
      // Ping service removed - version 3.x incompatible with libp2p 1.x
      pubsub: gossipsub({
        allowPublishToZeroTopicPeers: true,
        emitSelf: false,
        // Allow self messages for local testing
        ...(isDev && { emitSelf: true }),
        // Lower mesh requirements for small networks (relay + few peers)
        D: 1, // Target mesh size (default 6)
        Dlo: 0, // Minimum mesh size (default 4)
        Dhi: 12, // Maximum mesh size (default 12)
        Dscore: 1, // Minimum score for mesh (default 4)
        Dout: 0, // Outbound connections in mesh (default 2)
        // Enable floodsub fallback for more reliable message delivery
        fallbackToFloodsub: true,
        floodPublish: true, // Flood publish to all peers, not just mesh
        // More lenient scoring
        gossipFactor: 0.5, // Gossip to 50% of peers
        // Faster mesh formation
        fanoutTTL: 60000, // Keep fanout peers for 1 minute
      }),
    },
    // Only add bootstrap discovery if we have valid nodes
    peerDiscovery: validBootstrapList.length > 0
      ? [
          bootstrap({
            list: validBootstrapList,
          }),
        ]
      : [],
    connectionManager: {
      minConnections: 0, // Allow 0 connections when relay is unavailable
      maxConnections: 50,
      dialTimeout: 30000, // 30 second dial timeout
    },
  })

  // Start the node
  await node.start()

  console.log('[P2P] Node started with peer ID:', node.peerId.toString())
  console.log('[P2P] Bootstrap nodes:', validBootstrapList)
  console.log('[P2P] Listening on:', node.getMultiaddrs().map(a => a.toString()))

  // Track relay connection state
  let isConnectedToRelay = false
  let isReconnecting = false // Guard flag to prevent concurrent reconnection attempts
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let keepaliveInterval: ReturnType<typeof setInterval> | null = null

  // Capture node reference for closure
  const nodeRef = node

  // Keepalive to prevent relay from closing idle connections
  // Re-dial relay if connection is lost
  const startKeepalive = () => {
    if (keepaliveInterval) return

    // More aggressive keepalive - check every 10 seconds
    keepaliveInterval = setInterval(async () => {
      if (!nodeRef) return
      // Don't run if already reconnecting (prevents concurrent reconnection attempts)
      if (isReconnecting) return

      try {
        const connections = nodeRef.getConnections()
        const relayConnection = connections.find(conn =>
          validBootstrapList.some(addr => addr.includes(conn.remotePeer.toString()))
        )

        if (!relayConnection) {
          // Connection lost, try to re-establish immediately
          console.log('[P2P] Keepalive: no relay connection, re-dialing...')
          isConnectedToRelay = false
          isReconnecting = true

          try {
            for (const addr of validBootstrapList) {
              try {
                const { multiaddr } = await import('@multiformats/multiaddr')
                const ma = multiaddr(addr)
                await nodeRef.dial(ma, { signal: AbortSignal.timeout(15000) })
                console.log('[P2P] Keepalive: reconnected to relay')
                isConnectedToRelay = true
                break
              } catch {
                // Continue to next address
              }
            }
          } finally {
            isReconnecting = false
          }
        } else {
          // Connection is alive - log detailed status
          console.log('[P2P] Keepalive: relay connection stable', {
            totalConnections: connections.length,
            relayPeer: relayConnection.remotePeer.toString().substring(0, 12) + '...',
            relayAddr: relayConnection.remoteAddr?.toString(),
            status: relayConnection.status,
            direction: relayConnection.direction,
            streams: relayConnection.streams.length,
          })
        }
      } catch (err) {
        // Only log the error - don't reset isReconnecting here since it's only set
        // in the reconnection code path which has its own finally block
        console.warn('[P2P] Keepalive check failed:', err)
      }
    }, 10000) // Check every 10 seconds (more frequent)

    console.log('[P2P] Started keepalive checks')
  }

  const stopKeepalive = () => {
    if (keepaliveInterval) {
      clearInterval(keepaliveInterval)
      keepaliveInterval = null
      console.log('[P2P] Stopped keepalive pings')
    }
  }

  // Store cleanup function for node shutdown
  setCleanupKeepalive(stopKeepalive)

  // Function to reconnect to relay
  const reconnectToRelay = async () => {
    if (isConnectedToRelay || isReconnecting || !nodeRef) return

    isReconnecting = true
    try {
      for (const addr of validBootstrapList) {
        try {
          const { multiaddr } = await import('@multiformats/multiaddr')
          const ma = multiaddr(addr)
          console.log('[P2P] Reconnecting to relay...')
          await nodeRef.dial(ma, { signal: AbortSignal.timeout(30000) })
          console.log('[P2P] Reconnected to relay!')
          isConnectedToRelay = true
          return
        } catch (err) {
          console.error('[P2P] Reconnect failed:', err)
        }
      }
    } finally {
      isReconnecting = false
    }
  }

  // Log connection events
  node.addEventListener('peer:connect', (evt) => {
    const peerId = evt.detail.toString()

    // Get connection details for this peer
    const connections = nodeRef?.getConnections(evt.detail) ?? []
    const conn = connections[0]
    const connInfo = conn ? {
      count: connections.length,
      remoteAddr: conn.remoteAddr?.toString(),
      status: conn.status,
      direction: conn.direction,
      streams: conn.streams?.length ?? 0,
    } : { count: 0 }

    console.log('[P2P] Connected to peer:', peerId, connInfo)

    // Check if this is the relay node
    if (validBootstrapList.some(addr => addr.includes(peerId))) {
      isConnectedToRelay = true
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }
      // Start keepalive checks to maintain connection
      startKeepalive()
    }
  })

  node.addEventListener('peer:disconnect', (evt) => {
    const peerId = evt.detail.toString()
    console.log('[P2P] Disconnected from peer:', peerId)

    // If relay disconnected, schedule immediate reconnection
    if (validBootstrapList.some(addr => addr.includes(peerId))) {
      isConnectedToRelay = false
      console.log('[P2P] Relay disconnected, scheduling quick reconnection...')

      // Schedule reconnect after 1 second (faster recovery)
      if (!reconnectTimeout) {
        reconnectTimeout = setTimeout(async () => {
          reconnectTimeout = null
          await reconnectToRelay()
        }, 1000)
      }
    }
  })

  // Log peer discovery events
  node.addEventListener('peer:discovery', (evt) => {
    console.log('[P2P] Discovered peer:', evt.detail.id.toString(), evt.detail.multiaddrs.map(a => a.toString()))
  })

  // Log connection errors
  node.addEventListener('connection:close', (evt) => {
    console.log('[P2P] Connection closed:', evt.detail.remoteAddr?.toString())
  })

  // Try to connect to bootstrap nodes explicitly with retry
  if (validBootstrapList.length > 0) {
    console.log('[P2P] Attempting to dial bootstrap nodes...')
    for (const addr of validBootstrapList) {
      // Retry connection up to 3 times with increasing timeouts
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { multiaddr } = await import('@multiformats/multiaddr')
          const ma = multiaddr(addr)
          console.log(`[P2P] Dialing (attempt ${attempt}/3):`, addr)

          // Create abort controller with longer timeout (30 seconds)
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 30000)

          await node.dial(ma, { signal: controller.signal })
          clearTimeout(timeoutId)

          console.log('[P2P] Successfully connected to bootstrap node!')
          break // Success, exit retry loop
        } catch (err) {
          console.error(`[P2P] Failed to connect to bootstrap node (attempt ${attempt}/3):`, addr, err)
          if (attempt < 3) {
            console.log(`[P2P] Retrying in ${attempt * 2} seconds...`)
            await new Promise(r => setTimeout(r, attempt * 2000))
          }
        }
      }
    }
  }

  return node
}

/**
 * Get the current libp2p node instance
 */
export function getP2PNode(): Libp2p | null {
  return node
}

// Keep a reference to the cleanup function
let cleanupKeepalive: (() => void) | null = null

export function setCleanupKeepalive(cleanup: () => void) {
  cleanupKeepalive = cleanup
}

/**
 * Stop the libp2p node
 */
export async function stopP2PNode(): Promise<void> {
  if (node) {
    // Cleanup keepalive interval
    if (cleanupKeepalive) {
      cleanupKeepalive()
      cleanupKeepalive = null
    }
    await node.stop()
    node = null
    console.log('[P2P] Node stopped')
  }
}

/**
 * Get the peer ID of the current node
 */
export function getPeerId(): string | null {
  return node?.peerId.toString() ?? null
}

/**
 * Get the count of connected peers
 */
export function getConnectedPeerCount(): number {
  return node?.getConnections().length ?? 0
}

/**
 * Check if the node is connected to any peers
 */
export function isConnected(): boolean {
  return getConnectedPeerCount() > 0
}
